// Node Modules Begin
import { registerLocaleData } from '@angular/common';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeEs from '@angular/common/locales/es';
import { NgModule, APP_INITIALIZER, LOCALE_ID } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MsalModule, MsalService, MsalGuardConfiguration, MsalInterceptorConfiguration, MSAL_INSTANCE, MSAL_GUARD_CONFIG, MSAL_INTERCEPTOR_CONFIG } from '@azure/msal-angular';
import { PublicClientApplication, InteractionType, IPublicClientApplication } from '@azure/msal-browser';
import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { MaterialModule } from './angular-material/material.module';
// Node Modules End

// Routing Begin
import { AppRoutingModule } from './app-routing.module';
// End

// Services Begin
import { AppInitializeService } from './_services/app-initialize.service';
import { AppService } from './_services/app.service';
import { CustomLoader } from './_services/custom-loader';
// Services End

// Pipes Begin
import { SafePipeModule } from './_pipes/safe.module';
// Pipes End

// Guards Begin
import { PendingChangesGuard } from './_guard/pending-changes.guard';
//Guards End

import { AppComponent } from './app.component';

// Administrator Components Begin
import { EventLogsComponent } from './administration/event-logs/event-logs.component';
import { MaintenanceComponent } from './administration/maintenance/maintenance.component';
import { AddVendorComponent } from './administration/maintenance/add-vendor/add-vendor.component';
import { ManageAccountsComponent } from './administration/manage-accounts/manage-accounts.component';
import { AddAccountComponent } from './administration/manage-accounts/add-account/add-account.component';
import { AddUserAccountComponent } from './administration/manage-accounts/add-user-account/add-user-account.component';
import { AddServiceAccountComponent } from './administration/manage-accounts/add-service-account/add-service-account.component';
import { ManageCalendarComponent } from './administration/manage-calendar/manage-calendar.component';
import { AddHolidayComponent } from './administration/manage-calendar/add-holiday/add-holiday.component';
import { ManageCommunicationsComponent } from './administration/manage-communications/manage-communications.component';
import { AddScheduleComponent } from './administration/manage-communications/add-schedule/add-schedule.component';
import { ManageGeneralMarketsComponent } from './administration/manage-general-markets/manage-general-markets.component';
import { AddGeneralMarketComponent } from './administration/manage-general-markets/add-general-market/add-general-market.component';
import { AddInstrumentComponent } from './administration/manage-general-markets/add-instrument/add-instrument.component';
import { AddTestComponent } from './administration/manage-general-markets/add-test/add-test.component';
import { MonitorMarketsComponent } from './administration/monitor-markets/monitor-markets.component';
import { MonitorComponent } from './administration/monitor-markets/monitor/monitor.component';
import { DisplayDetailsComponent } from './administration/monitor-markets/monitor/display-details/display-details.component';
import { MarketContributeComponent } from './administration/monitor-markets/monitor/market-contribute/market-contribute.component';
import { ContributionConfirmComponent } from './administration/monitor-markets/monitor/market-contribute/contribution-confirm/contribution-confirm.component';
import { ReportsComponent } from './administration/reports/reports.component';
import { AddReportComponent } from './administration/reports/add-report/add-report.component';
import { ShowReportComponent } from './administration/reports/show-report/show-report.component';
import { ManageMenusComponent } from './administration/manage-menus/manage-menus.component';
import { PreviousContributionsComponent } from './administration/monitor-markets/monitor/previous-contributions/previous-contributions.component';
// Administrator Components End

// Common Components Begin
import { HeaderComponent } from './common/header/header.component';
import { FooterComponent } from './common/footer/footer.component';
import { SidebarComponent } from './common/sidebar/sidebar.component';
import { HomeComponent } from './common/home/home.component';
import { AddonComponent } from './common/addon/addon.component';
import { JoditEditorComponent } from './common/editor/jodit-editor.component';
import { CurrentTimeComponent } from './common/current-time/current-time.component';
import { LogoutComponent } from './common/logout/logout.component';
import { SpinnerOverlayComponent } from './common/spinner-overlay/spinner-overlay.component';
// Common Components End

// Dialog Components Begin
import { DialogConfirmComponent } from './dialog/dialog-confirm/dialog-confirm.component';
import { DialogStatusComponent } from './dialog/dialog-status/dialog-status.component';
// Dialog Components End

// My General Markets Components Begin
import { ContributeComponent } from './my-general-markets/contribute/contribute.component';
import { AssessComponent } from './my-general-markets/assess/assess.component';
import { ContributionSummaryComponent } from './my-general-markets/contribution-summary/contribution-summary.component';
import { SpreadsheetsComponent } from './my-general-markets/spreadsheets/spreadsheets.component';
import { SupportingDocsComponent } from './my-general-markets/supporting-docs/supporting-docs.component';
import { ContributeTransactionalComponent } from './my-general-markets/contribute-transactional/contribute-transactional.component';
import { ContributeEditComponent } from './my-general-markets/contribute-transactional/contribute-edit/contribute-edit.component';
import { AddTradeComponent } from './my-general-markets/contribute-transactional/add-trade/add-trade.component';
import { AssessTransactionalComponent } from './my-general-markets/assess-transactional/assess-transactional.component';
// My General Markets Components End

// User Components Begin
import { LoginComponent } from './user/login/login.component';
import { AuthenticationComponent } from './user/authentication/authentication.component';
import { ResetPasswordComponent } from './user/reset-password/reset-password.component';
// User Components End

// Test Components Begin
import { TestMarketsComponent } from './test/test-markets/test-markets.component';
// Test Components End

// Tools Components Begin
import { ToolsManageAccountsComponent } from './tools/tools-manage-accounts/tools-manage-accounts.component';
import { ToolsAddUserAccountComponent } from './tools/tools-manage-accounts/add-user-account/tools-add-user-account.component';
import { CalendarComponent } from './tools/calendar/calendar.component';
import { SummaryComponent } from './tools/summary/summary.component';
import { ChangePasswordComponent } from './tools/change-password/change-password.component';
import { SearchComponent } from './tools/search/search.component';
// Tools Components End

registerLocaleData(localeEs);

export function loggerCallback(logLevel, message) {
  //console.log(message);
}

export function initializeApp(appInitService: AppInitializeService) {
  return () => appInitService.azureSettingsLoader();
}

export function MSALInstanceFactory(appInitService: AppInitializeService): IPublicClientApplication {
  const settings = appInitService.getAzureSettings();
  return new PublicClientApplication({
    auth: {
      clientId: settings.spaId,
      authority: `https://login.microsoftonline.com/${settings.tenantId}`,
      redirectUri: '/login'
    }
  });
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read']
    }
  };
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap: new Map([
      ['https://graph.microsoft.com/v1.0/me', ['user.read']]
    ])
  };
}

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    HomeComponent,
    FooterComponent,
    HeaderComponent,
    SidebarComponent,
    AddonComponent,
    DialogConfirmComponent,
    DialogStatusComponent,
    AuthenticationComponent,
    ResetPasswordComponent,
    ManageAccountsComponent,
    ToolsManageAccountsComponent,
    ToolsAddUserAccountComponent,
    AddAccountComponent,
    AddUserAccountComponent,
    AddServiceAccountComponent,
    EventLogsComponent,
    ManageGeneralMarketsComponent,
    AddGeneralMarketComponent,
    AddInstrumentComponent,
    ManageCalendarComponent,
    AddHolidayComponent,
    ManageCommunicationsComponent,
    JoditEditorComponent,
    AddScheduleComponent,
    CurrentTimeComponent,
    CalendarComponent,
    SummaryComponent,
    MonitorMarketsComponent,
    ContributeComponent,
    AssessComponent,
    ContributionSummaryComponent,
    MonitorMarketsComponent,
    MonitorComponent,
    DisplayDetailsComponent,
    MarketContributeComponent,
    ContributionConfirmComponent,
    ReportsComponent,
    MaintenanceComponent,
    AddVendorComponent,
    ChangePasswordComponent,
    SearchComponent,
    TestMarketsComponent,
    AddReportComponent,
    SpreadsheetsComponent,
    LogoutComponent,
    ManageMenusComponent,
    SupportingDocsComponent,
    ShowReportComponent,
    PreviousContributionsComponent,
    ContributeTransactionalComponent,
    AssessTransactionalComponent,
    ContributeEditComponent,
    AddTradeComponent,
    SpinnerOverlayComponent,
    AddTestComponent
  ],
  exports: [TranslateModule],
  imports: [
    AppRoutingModule,
    FormsModule,
    BrowserAnimationsModule,
    MaterialModule,
    SafePipeModule,
    MsalModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: CustomLoader,
        deps: [HttpClient]
      }
    })],
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    AppService,
    TranslateService,
    PendingChangesGuard,
    {
      provide: LOCALE_ID,
      useFactory: (appService: AppService) => appService.getLocalLanguage(),
      deps: [AppService]
    },
    MsalService,
    AppInitializeService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [AppInitializeService],
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
      deps: [AppInitializeService]
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    }
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
